import {
  Event,
  EventAssignment,
  JobAdmin,
  Memo,
  ProjectDepartmentAssignment,
  type Property,
  User,
} from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useCallback, useState } from 'react';

import { type AuthContextType } from '../../../../context/AuthContext';
import type {
  useEventCreateMutation,
  useEventUpdateMutation,
} from '../../../../hooks/react-query/useEventsQuery';
import { type DirtyModelFields } from '../../../../tools/helpers';
import {
  EventAssignmentClientService,
  EventClientService,
  MemoClientService,
  ProjectManagementClientService,
  UserClientService,
} from '../../../../tools/helpers';
import { getFieldMaskFromString } from '../../EmployeeDepartments/utils';
import { type ProjectFormValues } from '../components/ProjectDetails';
import { generateProjectName } from '../utils/generateProjectName';

const PROJECT_MANAGER_TITLE = 'Project Manager';

// Types
export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export type CustomerLevelData = {
  customerId: number;
  contactMethod?: string;
  enRouteCallNumber?: string;
  paymentAgreement?: string;
  referralSourceId?: number;
  contactPreferenceId?: number;
  referralSourceOther?: string;
};

type UseEventSubmitParams = {
  auth: AuthContextType;
  existingEvent?: Event;
  isEditing: boolean;
  selectedDate: string;
  property: Property | null;
  preservedDirtyFields: DirtyModelFields<Record<string, unknown>>;
  resetDirtyFields: () => void;
  onSubmit: (event: Event | { id: number; isProject?: boolean }) => void;
  onJobCreated?: () => void;
  onCancel?: () => void;
  /** Clear the persisted new-job wizard draft once a submit succeeds. */
  clearPersistedDraft?: () => void;
  eventCreateMutation: ReturnType<typeof useEventCreateMutation>;
  eventUpdateMutation: ReturnType<typeof useEventUpdateMutation>;
};

export const useEventSubmit = ({
  auth,
  existingEvent,
  isEditing,
  selectedDate,
  property,
  preservedDirtyFields,
  resetDirtyFields,
  onSubmit,
  onJobCreated,
  onCancel,
  clearPersistedDraft,
  eventCreateMutation,
  eventUpdateMutation,
}: UseEventSubmitParams) => {
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const submitJob = useCallback(
    async (
      eventPartial: Partial<Event>,
      dirtyFields?: DirtyModelFields<Record<string, unknown>>,
      customerLevelData?: CustomerLevelData,
      internalNotes?: string,
    ) => {
      if (!property) {
        throw new Error('Property is required to submit a job');
      }

      setStatus('submitting');

      if (isEditing && existingEvent) {
        const updateReq = Event.create({ id: existingEvent.id });
        const mergedDirtyFields = { ...preservedDirtyFields, ...dirtyFields };

        const fieldMapping: Record<string, keyof Event> = {
          departmentId: 'departmentId',
          logJobStatus: 'logJobStatus',
          description: 'description',
          name: 'name',
          logNotes: 'logNotes',
          technicianIds: 'logTechnicianAssigned',
          jobTypeId: 'jobTypeId',
          jobSubtypeId: 'jobSubtypeId',
          logPaymentType: 'logPaymentType',
          amountQuoted: 'amountQuoted',
          isCallback: 'isCallback',
          callbackOriginalId: 'callbackOriginalId',
          isLmpc: 'isLmpc',
          highPriority: 'highPriority',
          dateStarted: 'dateStarted',
          dateEnded: 'dateEnded',
          isResidential: 'isResidential',
          isNewCustomer: 'isNewCustomer',
          serviceChannelWo: 'serviceChannelWo',
          slaDue: 'slaDue',
          customerPriorityId: 'customerPriorityId',
        };

        // Build field mask using Event API field names (form fields like technicianIds map to logTechnicianAssigned)
        const fieldMask: string[] = [];
        Object.entries(mergedDirtyFields).forEach(([formField, isDirty]) => {
          if (isDirty && fieldMapping[formField]) {
            const eventField = fieldMapping[formField];
            const maskName = getFieldMaskFromString(eventField);
            if (!fieldMask.includes(maskName)) fieldMask.push(maskName);
          }
        });

        Object.entries(mergedDirtyFields).forEach(([formField, isDirty]) => {
          if (isDirty && fieldMapping[formField]) {
            const eventField = fieldMapping[formField];
            const value = (eventPartial as any)[eventField];
            if (value !== undefined) {
              (updateReq as any)[eventField] = value;
            }
          }
        });

        if (mergedDirtyFields.timeWindowId && eventPartial.timeStarted && eventPartial.timeEnded) {
          updateReq.timeStarted = eventPartial.timeStarted;
          updateReq.timeEnded = eventPartial.timeEnded;
          if (!fieldMask.includes('TimeStarted')) fieldMask.push('TimeStarted');
          if (!fieldMask.includes('TimeEnded')) fieldMask.push('TimeEnded');
        }

        // When form supplies full dateStarted/dateEnded (e.g. job info edit with start/end date & time), they are already applied via fieldMapping.
        // Otherwise use single selectedDate + times (legacy flow).
        const hasFullDateRange = eventPartial.dateStarted != null && eventPartial.dateEnded != null;
        if (!hasFullDateRange) {
          const originalDate = existingEvent?.dateStarted?.split(' ')[0];
          const dateChanged = selectedDate !== originalDate;
          if (dateChanged) {
            updateReq.dateStarted = `${selectedDate} ${eventPartial.timeStarted || existingEvent?.timeStarted || '00:00'}`;
            updateReq.dateEnded = `${selectedDate} ${eventPartial.timeEnded || existingEvent?.timeEnded || '00:00'}`;
            updateReq.timeStarted =
              eventPartial.timeStarted || existingEvent?.timeStarted || '00:00';
            updateReq.timeEnded = eventPartial.timeEnded || existingEvent?.timeEnded || '00:00';

            if (!fieldMask.includes('DateStarted')) fieldMask.push('DateStarted');
            if (!fieldMask.includes('DateEnded')) fieldMask.push('DateEnded');
            if (!fieldMask.includes('TimeStarted')) fieldMask.push('TimeStarted');
            if (!fieldMask.includes('TimeEnded')) fieldMask.push('TimeEnded');
          }
        }

        if (mergedDirtyFields.technicianIds && eventPartial.logTechnicianAssigned !== undefined) {
          updateReq.logTechnicianAssigned = eventPartial.logTechnicianAssigned;
          if (!fieldMask.includes('LogTechnicianAssigned')) fieldMask.push('LogTechnicianAssigned');
        }

        updateReq.fieldMask = fieldMask;

        // Customer-level fields live on the User record, not the Event.
        // Only call User.Update if at least one of them is dirty.
        const customerFieldMask: string[] = [];
        if (mergedDirtyFields.contactMethod) customerFieldMask.push('PreferredContact');
        if (mergedDirtyFields.paymentAgreement) customerFieldMask.push('BillingTerms');
        if (mergedDirtyFields.enRouteCallNumber) customerFieldMask.push('EnRouteCall');
        if (mergedDirtyFields.referralSourceId) customerFieldMask.push('ReferralSourceId');
        if (mergedDirtyFields.contactPreferenceId) customerFieldMask.push('ContactPreferenceId');
        if (mergedDirtyFields.referralSourceOther) customerFieldMask.push('ReferralSourceOther');
        const hasCustomerChanges = !!customerLevelData && customerFieldMask.length > 0;
        const hasInternalNotes = !!internalNotes?.trim();
        const hasEventChanges = fieldMask.length > 0;

        if (!hasEventChanges && !hasCustomerChanges && !hasInternalNotes) {
          setStatus('idle');
          toast({ title: 'No changes to save', variant: 'default' });
          onSubmit(existingEvent);
          onCancel?.();
          return;
        }

        try {
          let updatedEvent: Event | undefined;

          if (hasEventChanges) {
            updatedEvent = await eventUpdateMutation.mutateAsync(updateReq);

            if (mergedDirtyFields.technicianIds) {
              const newTechnicianIds =
                eventPartial.logTechnicianAssigned?.split(',').filter(Boolean) || [];
              const assignmentReq = EventAssignment.create();
              assignmentReq.eventId = existingEvent.id;
              const existingAssignments =
                await EventAssignmentClientService.BatchGet(assignmentReq);

              const desiredIds = newTechnicianIds.map((id) => Number(id)).filter((id) => id !== 0);
              const existingIds = existingAssignments?.results?.map((a) => a.userId) || [];

              for (const userId of desiredIds) {
                if (!existingIds.includes(userId)) {
                  const assignment = EventAssignment.create();
                  assignment.userId = userId;
                  assignment.eventId = existingEvent.id;
                  await EventAssignmentClientService.Create(assignment);
                }
              }

              for (const existing of existingAssignments?.results || []) {
                if (!desiredIds.includes(existing.userId)) {
                  const assignmentToDelete = EventAssignment.create();
                  assignmentToDelete.id = existing.id;
                  await EventAssignmentClientService.Delete(assignmentToDelete);
                }
              }
            }
          }

          if (hasCustomerChanges) {
            const customerUpdate = User.create({
              id: customerLevelData!.customerId,
              preferredContact: customerLevelData!.contactMethod || '',
              billingTerms: customerLevelData!.paymentAgreement || '',
              enRouteCall: customerLevelData!.enRouteCallNumber || '',
              referralSourceId: customerLevelData!.referralSourceId || 0,
              contactPreferenceId: customerLevelData!.contactPreferenceId || 0,
              referralSourceOther: customerLevelData!.referralSourceOther || '',
              fieldMask: customerFieldMask,
            });
            await UserClientService.Update(customerUpdate);
          }

          if (hasInternalNotes) {
            const memo = Memo.create({
              eventId: existingEvent.id,
              description: internalNotes!,
              createdBy: auth.user.id,
              userId: auth.user.id,
              isInternal: true,
              isActive: true,
              classificationId: 15,
            });
            await MemoClientService.Create(memo);
          }

          toast({ title: 'Job updated', variant: 'success' });
          resetDirtyFields();
          clearPersistedDraft?.();
          onSubmit(updatedEvent || existingEvent);
          onJobCreated?.();
          onCancel?.();
          setStatus('success');
          return;
        } catch (err) {
          toast({
            title: 'Failed to update job',
            description: 'Please try again.',
            variant: 'destructive',
          });
          setStatus('error');
          throw err;
        }
      }

      try {
        const eventReq = Event.create({
          name:
            eventPartial.name ||
            (property.address
              ? `${property.address}${property.city ? ' ' + property.city : ''}${property.state ? ', ' + property.state : ''} ${property.zip ?? ''}`
              : 'Service Call'),
          propertyId: property.id,
          departmentId: eventPartial.departmentId ?? 0,
          logJobStatus: eventPartial.logJobStatus ?? 'Pending',
          logNotes: eventPartial.logNotes ?? '',
          description: eventPartial.description ?? '',
          dateStarted:
            eventPartial.dateStarted ?? `${selectedDate} ${eventPartial.timeStarted || '00:00'}`,
          dateEnded:
            eventPartial.dateEnded ?? `${selectedDate} ${eventPartial.timeEnded || '00:00'}`,
          timeStarted: eventPartial.timeStarted || '00:00',
          timeEnded: eventPartial.timeEnded || '00:00',
          isAllDay: 0,
          isActive: 1,
          logTechnicianAssigned: eventPartial.logTechnicianAssigned ?? '',
          jobTypeId: eventPartial.jobTypeId ?? 0,
          jobSubtypeId: eventPartial.jobSubtypeId ?? 0,
          logPaymentType: eventPartial.logPaymentType ?? 'Credit Card',
          amountQuoted: eventPartial.amountQuoted ?? '',
          isCallback: eventPartial.isCallback ?? 0,
          callbackOriginalId: eventPartial.callbackOriginalId ?? 0,
          diagnosticQuoted: eventPartial.diagnosticQuoted ?? 0,
          isLmpc: eventPartial.isLmpc ?? 0,
          highPriority: eventPartial.highPriority ?? 0,
          // No fallback: the details form requires an explicit sector, so defaulting here would
          // silently mislabel the job's sector.
          isResidential: eventPartial.isResidential,
          isNewCustomer: eventPartial.isNewCustomer ?? 0,
          logNotification: eventPartial.logNotification ?? '',
          logContractNotes: eventPartial.logContractNotes ?? '',
          notes: eventPartial.notes ?? '',
          serviceChannelWo: eventPartial.serviceChannelWo ?? '',
          slaDue: eventPartial.slaDue ?? '',
          customerPriorityId: eventPartial.customerPriorityId ?? 0,
        });

        const newEvent = await eventCreateMutation.mutateAsync(eventReq);

        // Create swallows failures and returns undefined; as 'success' the spinner never cleared.
        if (!newEvent) {
          setStatus('error');
          toast({
            title: "Couldn't confirm the job was created",
            description: 'Search for the job before trying again, so it isn’t booked twice.',
            variant: 'destructive',
          });
          return;
        }

        if (newEvent) {
          if (eventPartial.logTechnicianAssigned) {
            const ids = eventPartial.logTechnicianAssigned.split(',').filter(Boolean);
            await Promise.all(
              ids.map(async (idStr) => {
                const assignReq = EventAssignment.create({
                  eventId: newEvent.id,
                  userId: Number(idStr),
                });
                return await EventAssignmentClientService.Create(assignReq);
              }),
            );
          }

          // Update customer-level fields
          if (customerLevelData) {
            const customerUpdate = User.create({
              id: customerLevelData.customerId,
              preferredContact: customerLevelData.contactMethod || '',
              billingTerms: customerLevelData.paymentAgreement || '',
              enRouteCall: customerLevelData.enRouteCallNumber || '',
              referralSourceId: customerLevelData.referralSourceId || 0,
              contactPreferenceId: customerLevelData.contactPreferenceId || 0,
              referralSourceOther: customerLevelData.referralSourceOther || '',
              fieldMask: [
                'PreferredContact',
                'BillingTerms',
                'EnRouteCall',
                'ReferralSourceId',
                'ContactPreferenceId',
                'ReferralSourceOther',
              ],
            });
            await UserClientService.Update(customerUpdate);
          }

          // Create internal memo
          if (internalNotes?.trim()) {
            const memo = Memo.create({
              eventId: newEvent.id,
              description: internalNotes,
              createdBy: auth.user.id,
              userId: auth.user.id,
              isInternal: true,
              isActive: true,
              isPinned: true,
              classificationId: 15,
            });
            await MemoClientService.Create(memo);
          }

          toast({
            title: 'Job created',
            description: `Job #${newEvent.id} scheduled successfully`,
            variant: 'success',
          });

          resetDirtyFields();
          clearPersistedDraft?.();
          onSubmit(newEvent);
          onCancel?.();
        }

        setStatus('success');
      } catch (err) {
        setStatus('error');
        toast({
          title: 'Failed to create job',
          description: 'Please try again.',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [
      auth,
      existingEvent,
      isEditing,
      selectedDate,
      property,
      preservedDirtyFields,
      resetDirtyFields,
      onSubmit,
      onJobCreated,
      onCancel,
      clearPersistedDraft,
      eventCreateMutation,
      eventUpdateMutation,
    ],
  );

  const submitProject = useCallback(
    async (projectData: ProjectFormValues, customer: { businessname: string }) => {
      if (!property) {
        throw new Error('Property is required to submit a project');
      }

      setStatus('submitting');

      try {
        const primeDept = projectData.departmentAssignments.find((d) => d.isPrime);
        if (!primeDept) {
          toast({
            title: 'Error',
            description: 'Please select a prime department',
            variant: 'destructive',
          });
          setStatus('error');
          return;
        }

        const departmentScopes = projectData.departmentAssignments.map((dept) => ({
          departmentId: dept.departmentId,
          name: projectData.description ?? '',
          description: projectData.description ?? '',
        }));

        const createProjectReq = {
          name: projectData.description ?? '',
          propertyId: property.id,
          userId: property.userId,
          departmentScopes,
          primaryDepartmentId: primeDept.departmentId,
          poNumber: projectData.poNumber ?? '',
          projectStartDate: projectData.dateStarted,
          projectEndDate: projectData.dateEnded,
          dateStarted: `${projectData.dateStarted} 00:00`,
          dateEnded: `${projectData.dateEnded} 00:00`,
          description: projectData.description ?? '',
          // Pass notes to both field names to ensure backend receives it
          logNotes: projectData.logNotes ?? '',
          notes: projectData.logNotes ?? '',
        };

        const response =
          await ProjectManagementClientService.CreateProjectWithDepartmentScopes(createProjectReq);

        if (!response || !response.value) {
          throw new Error('Failed to create project');
        }

        const primaryEventId = response.value;

        // Auto-generate the project name: {businessname}:{eventId} {PO} {city}
        const projectName = generateProjectName({
          businessName: customer.businessname,
          eventId: primaryEventId,
          po: projectData.poNumber,
          city: property.city,
        });

        // Update the primary event with the generated name and notes
        // (CreateProjectWithDepartmentScopes doesn't support notes fields)
        await EventClientService.Update(
          Event.create({
            id: primaryEventId,
            name: projectName,
            notes: projectData.logNotes ?? '',
            logNotes: projectData.logNotes ?? '',
            logPaymentType: 'ACH / Wire',
            fieldMask: ['Name', 'Notes', 'LogNotes', 'LogPaymentType'],
          }),
        );

        // Create ProjectDepartmentAssignment records for each scope so they are
        // visible in the project detail view (scopes without assignments are hidden)
        await Promise.all(
          projectData.departmentAssignments.map((dept) =>
            ProjectManagementClientService.CreateProjectDepartmentAssignment(
              ProjectDepartmentAssignment.create({
                projectEventId: primaryEventId,
                departmentId: dept.departmentId,
                allocatedBudget: Number(dept.allocatedBudget) || 0,
                allocatedHours: Number(dept.allocatedHours) || 0,
                isActive: true,
              }),
            ),
          ),
        );

        // Create JobAdmin for project manager if selected
        if (projectData.projectManagerId) {
          try {
            await EventClientService.CreateJobAdmin(
              JobAdmin.create({
                jobNumber: primaryEventId,
                userId: projectData.projectManagerId,
                title: PROJECT_MANAGER_TITLE,
                isActive: true,
              }),
            );
          } catch (err) {
            console.error('Failed to assign project manager:', err);
            // Non-blocking - project was still created successfully
          }
        }

        toast({
          title: 'Project created',
          description: `Project #${primaryEventId} created successfully`,
          variant: 'success',
        });

        clearPersistedDraft?.();
        onSubmit({ id: primaryEventId, isProject: true });
        onJobCreated?.();
        onCancel?.();
        setStatus('success');
      } catch (err) {
        setStatus('error');
        console.error('Failed to create project', err);
        toast({
          title: 'Failed to create project',
          description: 'Please try again.',
          variant: 'destructive',
        });
      }
    },
    [property, onSubmit, onJobCreated, onCancel, clearPersistedDraft],
  );

  const resetStatus = useCallback(() => {
    setStatus('idle');
  }, []);

  const isSubmitting = status === 'submitting' || status === 'success';

  return {
    submitJob,
    submitProject,
    status,
    isSubmitting,
    resetStatus,
  };
};
